import { VideoStreamController } from './VideoStreamController';
import { VideoStreamSupportedMediaType } from '../types';
import { VLCVideoStreamControllerConfiguration } from './types';
import { isNullish } from '../../../Utility/Nullish';
import { AutoResetEvent, Task } from '@reachmedianetwork/tpl';
import CancellationToken from '@reachmedianetwork/tpl/dist/src/cancellationToken';
import { Log } from '../../../AuxillaryService/Log';
import { assert } from '../../../Utility/assert';
import { PromisedAll, Promisify } from '../../../Utility/Promisify';

type LibVLCControllerPromised = PromisedAll<Omit<LibVLCController, LibVLCControllerSyncFunctions>> &
    Pick<LibVLCController, 'RegisterVlcControlListener'>;

enum VlcStreamState {
    Stalled = 'stalled',
    Playing = 'playing',
    Ended = 'ended',
}

/**
 * Video Stream for Vlc
 *
 * type hdmi_windows type will have its src configuration autofilled
 * in the layout editor as 'dshow://'
 * @see {@link https://wiki.videolan.org/Documentation:Modules/dshow/}
 */
export class VideoStreamLibVLCController extends VideoStreamController {
    readonly type: VideoStreamSupportedMediaType.VLC = VideoStreamSupportedMediaType.VLC;
    private configuration: VLCVideoStreamControllerConfiguration;

    streamElement: HTMLDivElement;

    #vlcControlID: number | undefined = undefined;
    readonly #vlcApiPromised: LibVLCControllerPromised;
    #streamMonitorTask?: Task<void> = undefined;
    readonly #streamStateChangedEvent = new AutoResetEvent();
    #streamState: VlcStreamState = VlcStreamState.Stalled;
    #lastPlayingSignalReceivedAt = 0;

    constructor(configuration: VLCVideoStreamControllerConfiguration, vlcApi: LibVLCController) {
        super();

        this.configuration = configuration;

        this.#vlcApiPromised = {
            CreateVlcControlAsync: Promisify(vlcApi.CreateVlcControlAsync),
            HideVlcControlAsync: Promisify(vlcApi.HideVlcControlAsync),
            ShowVlcControlAsync: Promisify(vlcApi.ShowVlcControlAsync),
            DestroyVlcControlAsync: Promisify(vlcApi.DestroyVlcControlAsync),
            DestroyAllVlcControlsAsync: Promisify(vlcApi.DestroyAllVlcControlsAsync),
            PauseVlcControlAsync: Promisify(vlcApi.PauseVlcControlAsync),
            StopVlcControlAsync: Promisify(vlcApi.StopVlcControlAsync),
            ResumeVlcControlAsync: Promisify(vlcApi.ResumeVlcControlAsync),
            PlayVlcControlAsync: Promisify(vlcApi.PlayVlcControlAsync),
            SetControlVolumeAsync: Promisify(vlcApi.SetControlVolumeAsync),
            RegisterVlcControlListener: vlcApi.RegisterVlcControlListener,
        };

        this.streamElement = document.createElement('div');
        this.streamElement.style.height = '100%';
        this.streamElement.style.width = '100%';
    }

    #transitionState = async (newState: VlcStreamState): Promise<void> => {
        if (this.#streamState !== newState) {
            this.#streamState = newState;
            await this.#streamStateChangedEvent.set();
        }
    };

    #onVlcPlayerEvent = (event: VlcEvent): void => {
        switch (event.Name) {
            case 'Buffering':
            case 'EncounteredError':
            case 'Opening':
                void this.#transitionState(VlcStreamState.Stalled);

                break;
            case 'EndReached':
            case 'Stopped':
                void this.#transitionState(VlcStreamState.Ended);

                break;
            case 'Playing':
            case 'PositionChanged':
                this.#lastPlayingSignalReceivedAt = Date.now();

                void this.#transitionState(VlcStreamState.Playing);
                break;
        }
    };

    #monitorVlcStreamRoutine = async (cancellationToken: CancellationToken): Promise<void> => {
        let recoveryBackoffMs = 0;

        while (!cancellationToken.isCancellationRequested) {
            Log.Debug(`tick VLC control ${this.#vlcControlID}`, null, __fileline);

            //wait for state change or 1 second, whichever is first
            if (
                (await this.#streamStateChangedEvent.waitOne(1000, cancellationToken.split())) &&
                this.#streamState === VlcStreamState.Playing
            ) {
                Log.Info(
                    `video stream has state 'Playing' for VLC control ${this.#vlcControlID}`,
                    null,
                    __fileline
                );

                recoveryBackoffMs = 0;
            }

            //if the stream ended or has stalled for >= 5 seconds
            if (
                this.#streamState === VlcStreamState.Ended ||
                Date.now() - this.#lastPlayingSignalReceivedAt >= 5000
            ) {
                Log.Warning(
                    `video stream (VLC control ${
                        this.#vlcControlID
                    }) has ended or stalled; attempting to recover stream`,
                    null,
                    __fileline
                );

                //attempt to recover it
                if (recoveryBackoffMs > 0) {
                    Log.Info(
                        `waiting backoff of ${recoveryBackoffMs} msec(s) before recovering stream for VLC control ${
                            this.#vlcControlID
                        }`,
                        null,
                        __fileline
                    );

                    await Task.delay(Math.min(recoveryBackoffMs, 60000), cancellationToken.split());

                    recoveryBackoffMs *= 1.5;
                } else {
                    //first recovery attempt, next recovery have 1 second backoff
                    recoveryBackoffMs = 1000;
                }

                try {
                    assert(this.#vlcApiPromised, '#vlcApiPromised is not defined');
                    assert(this.#vlcControlID, '#vlcControlId is not defined');

                    Log.Debug(`stopping VLC control ${this.#vlcControlID}`, null, __fileline);
                    await this.#vlcApiPromised.StopVlcControlAsync(this.#vlcControlID);
                    Log.Debug(`starting VLC control ${this.#vlcControlID}`, null, __fileline);
                    await Promise.race([
                        this.#vlcApiPromised.PlayVlcControlAsync(this.#vlcControlID),
                        Task.delay(1000, cancellationToken.split()),
                    ]);
                } catch (e) {
                    Log.Error(
                        `an error occurred attempting to recover VLC control ${this.#vlcControlID}`,
                        null,
                        __fileline,
                        e
                    );
                }
            }
        }

        Log.Debug(
            `monitoring task ending for VLC control ${this.#vlcControlID}` +
                JSON.stringify({
                    isCancellationRequest: cancellationToken.isCancellationRequested,
                    vlcControlId: this.#vlcControlID,
                    api: this.#vlcApiPromised,
                }),
            null,
            __fileline
        );
    };

    async canBePlayed(): Promise<boolean> {
        return typeof this.configuration.url === 'string' && this.configuration.url.length > 0;
    }

    async play(): Promise<void> {
        //set #lastPlayingSignalReceivedAt to now so we don't stall immediately
        this.#lastPlayingSignalReceivedAt = Date.now();

        if (isNullish(this.#vlcControlID)) {
            //VLC control is not created, do so now
            const bounds = this.streamElement.getBoundingClientRect();
            const vlcControlID = (this.#vlcControlID =
                await this.#vlcApiPromised.CreateVlcControlAsync(
                    this.configuration.url,
                    Math.round(bounds.left),
                    Math.round(bounds.top),
                    Math.round(bounds.width),
                    Math.round(bounds.height),
                    this.configuration.aspect_ratio || '',
                    this.configuration.video_device || '',
                    isNullish(this.configuration.vlc_options)
                        ? null
                        : this.configuration.vlc_options.split(' '),
                    true //auto-play
                ));

            if (isNullish(vlcControlID)) {
                throw new Error('failed to instantiate VLC control');
            }

            //listen for events from VLC media player control
            this.#vlcApiPromised.RegisterVlcControlListener(vlcControlID, this.#onVlcPlayerEvent);
        } else {
            //VLC control already exists, just start playing it
            await this.#vlcApiPromised.PlayVlcControlAsync(this.#vlcControlID);
            await this.#vlcApiPromised.ShowVlcControlAsync(this.#vlcControlID);
        }

        if (isNullish(this.#streamMonitorTask)) {
            this.#streamMonitorTask = new Task(this.#monitorVlcStreamRoutine);
        }
    }

    async pause(): Promise<void> {
        //observing the signage, it is noted that when this widget throws an error, that error is
        //rendered within the widget DOM element. Presumably, the color and background of the DOM
        //element is being set here to facilitate readability of that error message.
        if (this._isPlaying) {
            this.streamElement.style.color = 'white';
            this.streamElement.style.backgroundColor = 'black';
        }

        //set _isPlaying to false before calling StopVlcControlAsync otherwise we will trigger
        //automatic re-play of the stream media.
        this._isPlaying = false;

        if (!isNullish(this.#vlcControlID)) {
            if (!isNullish(this.#streamMonitorTask)) {
                await this.#streamMonitorTask.cancel();
                this.#streamMonitorTask = undefined;
            }

            await this.#vlcApiPromised.StopVlcControlAsync(this.#vlcControlID);
            await this.#vlcApiPromised.HideVlcControlAsync(this.#vlcControlID);
        }
    }

    async stop(): Promise<void> {
        if (!isNullish(this.#vlcControlID)) {
            try {
                if (!isNullish(this.#streamMonitorTask)) {
                    await this.#streamMonitorTask.cancel();
                    this.#streamMonitorTask = undefined;
                }

                await this.#vlcApiPromised.DestroyVlcControlAsync(this.#vlcControlID);
            } finally {
                this.#vlcControlID = undefined;
            }
        }

        await super.stop();
    }
}
